import {i18n} from '@online-offer/i18n';
import {Box, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {ReactNode} from 'react';

import {isNotEmpty, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {formatFinancialProductMoney} from './formatFinancialProductMoney';
import {formatLeasingCostAmount} from './formatLeasingCostAmount';
import {LeasingCostSections} from './getLeasingCostSections';

interface FinancialProductLeasingCostsProps extends TestIdProps {
  sections: Pick<LeasingCostSections, 'downPayment' | 'paidOnce' | 'conditional'>;
}

/**
 * What a leasing costs outside its monthly payment, one card per kind: the down payment, what is
 * paid once on top, and what is charged only when it happens. A card without content is left out.
 */
export function FinancialProductLeasingCosts(props: FinancialProductLeasingCostsProps) {
  const formatCurrency = useFormatCurrency();

  return (
    <VStack spacing={3} data-testid={suffixTestId('leasingCosts', props)}>
      <Show when={props.sections.downPayment}>
        {(downPayment) => (
          <LeasingCostCard
            title={i18n.t('financialProducts.downPayment')}
            description={i18n.t('financialProducts.leasingCosts.downPayment.description')}
            data-testid={suffixTestId('leasingDownPayment', props)}
          >
            <Heading size={4}>{formatFinancialProductMoney(downPayment, formatCurrency)}</Heading>
          </LeasingCostCard>
        )}
      </Show>
      <Show when={isNotEmpty(props.sections.paidOnce)}>
        <LeasingCostCard
          title={i18n.t('financialProducts.leasingCosts.paidOnce.title')}
          description={i18n.t('financialProducts.leasingCosts.paidOnce.description')}
          data-testid={suffixTestId('leasingPaidOnce', props)}
        >
          {props.sections.paidOnce.map((item) => (
            <LeasingCostRow
              key={item.id}
              label={i18n.t(item.labelKey)}
              value={formatLeasingCostAmount(item, formatCurrency)}
            />
          ))}
        </LeasingCostCard>
      </Show>
      <Show when={isNotEmpty(props.sections.conditional)}>
        <LeasingCostCard
          title={i18n.t('financialProducts.leasingCosts.conditional.title')}
          description={i18n.t('financialProducts.leasingCosts.conditional.description')}
          data-testid={suffixTestId('leasingConditional', props)}
        >
          {props.sections.conditional.map((item) => (
            <LeasingCostRow
              key={item.id}
              label={i18n.t(item.labelKey)}
              hint={isNotNil(item.tooltipKey) ? i18n.t(item.tooltipKey) : null}
              value={formatLeasingCostAmount(item, formatCurrency)}
            />
          ))}
        </LeasingCostCard>
      </Show>
    </VStack>
  );
}

interface LeasingCostCardProps extends TestIdProps {
  title: string;
  description: string;
  children: ReactNode;
}

function LeasingCostCard(props: LeasingCostCardProps) {
  return (
    <Box
      padding={4}
      borderRadius="medium"
      border="thin solid"
      borderColor="border.emphasisDefault"
      backgroundColor="fill.white"
      data-testid={props['data-testid']}
    >
      <VStack spacing={3}>
        <VStack spacing={1}>
          <Text size="small" alternative>
            {props.title}
          </Text>
          <Text size="xSmall" color="secondary">
            {props.description}
          </Text>
        </VStack>
        {props.children}
      </VStack>
    </Box>
  );
}

interface LeasingCostRowProps {
  label: string;
  hint?: string | Nullish;
  value: string;
}

function LeasingCostRow(props: LeasingCostRowProps) {
  return (
    <HStack justify="space-between" spacing={3} align="baseline">
      <VStack spacing={1} minWidth={0}>
        <Text size="small" overflowWrap="anywhere">
          {props.label}
        </Text>
        <Show when={isNotNilOrEmpty(props.hint) ? props.hint : null}>
          {(hint) => (
            <Text size="xSmall" color="tertiary" overflowWrap="anywhere">
              {hint}
            </Text>
          )}
        </Show>
      </VStack>
      <Text size="small" alternative align="right" noWrap>
        {props.value}
      </Text>
    </HStack>
  );
}
